/**
 * The one DD-MM-YYYY formatter (Commandment XII: DD-MM-YYYY in the UI; the wire stays ISO-8601).
 * Every other module re-exports from here — do not add another copy.
 *
 * Always rendered in Asia/Kolkata (IST), never the browser's local zone: the platform is India-first
 * and the backend training certificate prints IST (training/certificate.ts), so the same instant must
 * show the same calendar day on screen, in exports and on the certificate regardless of where the
 * viewer's machine is set. Browser-local formatting made an ISO instant like 2026-09-29T00:00:00Z
 * read 28-09 for any viewer west of UTC.
 */
export const DISPLAY_TIME_ZONE = 'Asia/Kolkata';

const FMT = new Intl.DateTimeFormat('en-GB', {
    timeZone: DISPLAY_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

// Fixed names: ICU builds disagree (en-GB prints "Sept"), and the UI must read the same everywhere.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EM_DASH = '-';
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A bare calendar date ("2026-09-29") has no instant: reformat its civil fields verbatim so no zone can shift the day. */
function dateOnlyParts(value) {
    const m = typeof value === 'string' ? DATE_ONLY.exec(value.trim()) : null;
    if (!m) return null;
    const [, y, mo, d] = m;
    const probe = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
    if (probe.getUTCFullYear() !== Number(y) || probe.getUTCMonth() !== Number(mo) - 1 || probe.getUTCDate() !== Number(d)) return null;
    return { day: d, month: mo, year: y };
}

function parts(iso) {
    if (iso === null || iso === undefined || iso === '') return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const p = {};
    for (const { type, value } of FMT.formatToParts(d)) p[type] = value;
    return p;
}

/** DD-MM-YYYY (IST). Accepts ISO instants, epoch ms, Date, or a date-only "YYYY-MM-DD" (kept as that calendar day). */
export function formatDdMmYyyy(iso) {
    const civil = dateOnlyParts(iso);
    if (civil) return `${civil.day}-${civil.month}-${civil.year}`;
    const p = parts(iso);
    return p ? `${p.day}-${p.month}-${p.year}` : EM_DASH;
}

/** DD-MM-YYYY HH:mm (24h, IST). A date-only value has no time of day, so it renders as the date alone. */
export function formatDdMmYyyyTime(iso) {
    const civil = dateOnlyParts(iso);
    if (civil) return `${civil.day}-${civil.month}-${civil.year}`;
    const p = parts(iso);
    return p ? `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}` : EM_DASH;
}

/** DD-MM-YYYY HH:mm IST — for schedule/meeting times where the zone must be stated. */
export function formatDdMmYyyyTimeIst(iso) {
    if (dateOnlyParts(iso)) return formatDdMmYyyy(iso);
    const out = formatDdMmYyyyTime(iso);
    return out === EM_DASH ? out : `${out} IST`;
}

const TIME_HMS = new Intl.DateTimeFormat('en-GB', {
    timeZone: DISPLAY_TIME_ZONE, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

/** HH:mm:ss (24h, IST) of an instant; em dash when missing or date-only. */
export function formatTimeHmsIst(iso) {
    if (dateOnlyParts(iso) || iso === null || iso === undefined || iso === '') return EM_DASH;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? EM_DASH : TIME_HMS.format(d);
}

/** "Tue, 29-09-2026" (IST day; a date-only value keeps its own day). */
export function formatWeekdayDdMmYyyy(iso) {
    const date = formatDdMmYyyy(iso);
    if (date === EM_DASH) return date;
    const [dd, mm, yyyy] = date.split('-');
    // Noon UTC on the civil day is the same weekday in every zone.
    const wd = WEEKDAYS[new Date(Date.UTC(+yyyy, +mm - 1, +dd, 12)).getUTCDay()];
    return `${wd}, ${date}`;
}

/** Calendar-tile pieces for the IST day of a meeting: { month: 'Sep', day: '29' }. */
export function istMonthDay(iso) {
    const date = formatDdMmYyyy(iso);
    if (date === EM_DASH) return { month: '', day: '' };
    const [dd, mm, yyyy] = date.split('-');
    const month = MONTHS[Number(mm) - 1].slice(0, 3);
    return { month, day: String(Number(dd)) };
}

/** The IST calendar date of an instant as "YYYY-MM-DD" (for date-picker values that mirror what the UI displays). */
export function istDateInputValue(iso) {
    const date = formatDdMmYyyy(iso);
    if (date === EM_DASH) return '';
    const [dd, mm, yyyy] = date.split('-');
    return `${yyyy}-${mm}-${dd}`;
}

/**
 * "29 September 2026" in IST — only for legal-document prose (contract effective date) where the
 * house style spells the month out. UI chrome uses formatDdMmYyyy.
 */
export function formatLongDateIst(iso) {
    const date = formatDdMmYyyy(iso);
    if (date === EM_DASH) return date;
    const [dd, mm, yyyy] = date.split('-');
    const month = MONTHS[Number(mm) - 1];
    return `${Number(dd)} ${month} ${yyyy}`;
}
